import React, { useState, useEffect, useRef } from 'react';
import { XMarkIcon, Bars3Icon } from '@heroicons/react/24/outline';
import { PitchBodyTemplate, PitchBodyField, ALL_PITCH_BODY_FIELDS, REQUIRED_PITCH_BODY_FIELD_KEYS } from '../../../../types/email';
import Button from '../../../ui/Button';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSave: (name: string, fields: PitchBodyField[]) => Promise<void>;
  template?: PitchBodyTemplate | null;
}

const isRequiredField = (key: string) => (REQUIRED_PITCH_BODY_FIELD_KEYS as readonly string[]).includes(key);

const buildDefaultFields = (): PitchBodyField[] =>
  ALL_PITCH_BODY_FIELDS.map((f, i) => ({ ...f, order: i, enabled: true }));

const PitchBodyModal: React.FC<Props> = ({ isOpen, onClose, onSave, template }) => {
  const [name, setName] = useState('');
  const [fields, setFields] = useState<PitchBodyField[]>(buildDefaultFields());
  const [saving, setSaving] = useState(false);
  const [nameError, setNameError] = useState('');

  const dragIndex = useRef<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    if (template) {
      setName(template.name);
      // Only fields in ALL_PITCH_BODY_FIELDS are offered; saved keys no longer in it are dropped
      const savedByKey = new Map(template.fields.map((s) => [s.key, s]));
      const merged: PitchBodyField[] = ALL_PITCH_BODY_FIELDS
        .filter((f) => savedByKey.has(f.key))
        .map((f) => savedByKey.get(f.key)!)
        .sort((a, b) => a.order - b.order);
      // Fields added after this template was saved go in right after their predecessor in
      // ALL_PITCH_BODY_FIELDS, disabled, instead of piling up at the end
      ALL_PITCH_BODY_FIELDS.forEach((f, i) => {
        if (savedByKey.has(f.key)) return;
        const prevKeys = ALL_PITCH_BODY_FIELDS.slice(0, i).map((p) => p.key);
        let insertAt = 0;
        merged.forEach((m, idx) => {
          if (prevKeys.includes(m.key)) insertAt = idx + 1;
        });
        merged.splice(insertAt, 0, { ...f, order: 0, enabled: false });
      });
      // Required fields are always enabled and pinned first, regardless of saved state/order
      const requiredFirst = [
        ...merged.filter((f) => isRequiredField(f.key)),
        ...merged.filter((f) => !isRequiredField(f.key)),
      ];
      setFields(
        requiredFirst.map((f, i) => ({ ...f, order: i, enabled: isRequiredField(f.key) ? true : f.enabled }))
      );
    } else {
      setName('');
      setFields(buildDefaultFields());
    }
    setNameError('');
  }, [isOpen, template]);

  const toggleField = (key: string) => {
    if (isRequiredField(key)) return;
    setFields((prev) =>
      prev.map((f) => (f.key === key ? { ...f, enabled: !f.enabled } : f))
    );
  };

  const optionalFields = fields.filter((f) => !isRequiredField(f.key));
  const allOptionalEnabled = optionalFields.length > 0 && optionalFields.every((f) => f.enabled);
  const someOptionalEnabled = optionalFields.some((f) => f.enabled);
  const selectAllCheckboxRef = useRef<HTMLInputElement | null>(null);

  const toggleAllFields = () => {
    const nextEnabled = !allOptionalEnabled;
    setFields((prev) =>
      prev.map((f) => (isRequiredField(f.key) ? f : { ...f, enabled: nextEnabled }))
    );
  };

  // No deps: the checkbox remounts each time the modal reopens, so re-apply on every render
  useEffect(() => {
    if (selectAllCheckboxRef.current) {
      selectAllCheckboxRef.current.indeterminate = someOptionalEnabled && !allOptionalEnabled;
    }
  });

  const handleDragStart = (index: number, key: string) => {
    if (isRequiredField(key)) return;
    dragIndex.current = index;
  };

  const handleDragOver = (e: React.DragEvent, index: number, key: string) => {
    e.preventDefault();
    if (isRequiredField(key) || isRequiredField(fields[dragIndex.current ?? index]?.key)) return;
    if (dragIndex.current === index) return;
    setDragOverIndex(index);
  };

  const handleDragEnd = () => {
    if (dragIndex.current === null || dragOverIndex === null) return;
    if (dragIndex.current !== dragOverIndex) {
      setFields((prev) => {
        const updated = [...prev];
        const [moved] = updated.splice(dragIndex.current!, 1);
        updated.splice(dragOverIndex, 0, moved);
        return updated.map((f, i) => ({ ...f, order: i }));
      });
    }
    dragIndex.current = null;
    setDragOverIndex(null);
  };

  const handleSave = async () => {
    if (!name.trim()) {
      setNameError('Name is required');
      return;
    }
    setSaving(true);
    try {
      await onSave(name.trim(), fields);
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 flex flex-col max-h-[90vh] border border-neutral-100">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-neutral-100">
          <div>
            <h2 className="text-sm font-semibold text-black">
              {template ? 'Edit Pitch Body' : 'New Pitch Body'}
            </h2>
            <p className="text-xs text-neutral-400 mt-0.5">
              {template ? 'Update your pitch body configuration' : 'Configure a reusable pitch body template'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1 transition-colors rounded-md text-neutral-400 hover:text-primary-700 hover:bg-primary-50 focus:outline-none focus-visible:border-primary-700 border border-transparent"
          >
            <XMarkIcon className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {/* Name */}
          <div>
            <label className="block text-xs font-semibold text-neutral-500 uppercase tracking-wider mb-1.5">
              Template Name <span className="text-danger-500">*</span>
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => { setName(e.target.value); setNameError(''); }}
              maxLength={50}
              placeholder="e.g., Standard Pitch"
              className="w-full px-3 py-2 text-sm border border-neutral-300 rounded-lg focus:border-primary-500 outline-none transition-all placeholder:text-neutral-300"
            />
            {nameError && <p className="text-xs text-danger-500 mt-1">{nameError}</p>}
          </div>

          {/* Fields */}
          <div>
            <div className="flex flex-col  mb-2">
              <div className='flex items-start justify-between gap-2'>
                <p className="text-xs font-semibold text-neutral-500 uppercase tracking-wider">Fields</p>
                <label className="flex items-center gap-1.5 text-xs font-medium text-neutral-600 whitespace-nowrap flex-shrink-0 cursor-pointer select-none">
                  {allOptionalEnabled ? 'Deselect all' : 'Select all'}
                  <input
                    ref={selectAllCheckboxRef}
                    type="checkbox"
                    checked={allOptionalEnabled}
                    onChange={toggleAllFields}
                    className="w-3.5 h-3.5 rounded cursor-pointer text-primary-700 border-neutral-300 focus:border-primary-700"
                  />
                </label>
              </div>
              <p className="text-xs text-neutral-400 mt-0.5">
                Toggle on/off and drag to reorder. Only enabled fields appear in the email. Artist Name, Release Title, and Release Date are required and always shown above the pitch body.
              </p>
            </div>
            <div className="space-y-1">
              {fields.map((field, index) => {
                const required = isRequiredField(field.key);
                return (
                <div
                  key={field.key}
                  draggable={!required}
                  onDragStart={() => handleDragStart(index, field.key)}
                  onDragOver={(e) => handleDragOver(e, index, field.key)}
                  onDragEnd={handleDragEnd}
                  className={`flex items-center gap-3 px-3 py-2 rounded-lg select-none transition-all border border-neutral-200 ${
                    required ? 'cursor-default' : 'cursor-grab active:cursor-grabbing'
                  } ${
                    dragOverIndex === index && dragIndex.current !== index
                      ? 'bg-primary-50 border-primary-300 shadow-sm'
                      : required
                      ? 'bg-primary-50/50 border-neutral-100'
                      : field.enabled
                      ? 'bg-white hover:border-primary-300 shadow-sm'
                      : ''
                  }`}
                >
                  <Bars3Icon className={`w-3.5 h-3.5 flex-shrink-0 ${required ? 'text-primary-200' : 'text-neutral-300'}`} />
                  <span className={`flex-1 text-sm text-neutral-700`}>
                    {field.label}
                  </span>
                  {required ? (
                    <span className="text-[10px] font-semibold text-primary-700 bg-primary-100 px-2 py-0.5 rounded-full flex-shrink-0">
                      Required
                    </span>
                  ) : (
                    <button
                      type="button"
                      onClick={() => toggleField(field.key)}
                      className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors flex-shrink-0 focus:outline-none ${
                        field.enabled ? 'bg-primary-700' : 'bg-neutral-100'
                      }`}
                    >
                      <span
                        className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${
                          field.enabled ? 'translate-x-4' : 'translate-x-0.5'
                        }`}
                      />
                    </button>
                  )}
                </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-5 py-3 border-t border-neutral-100 bg-neutral-50/50 rounded-b-2xl">
          <span className="text-xs text-neutral-400">
            {fields.filter(f => f.enabled).length} of {fields.length} fields enabled
          </span>
          <div className="flex gap-2">
            <Button
              variant="neutral"
              type="button"
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? 'Saving...' : template ? 'Update' : 'Create'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PitchBodyModal;
